import { useState, useEffect, useContext } from 'react';

import { Button, Grid, Box, TextField, MenuItem } from '@material-ui/core';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import { DataGrid } from '@mui/x-data-grid/DataGrid';
import LinearProgress from '@material-ui/core/LinearProgress';
import CssBaseline from '@mui/material/CssBaseline';
import { debounce } from 'lodash';

// context
import { format } from 'date-fns';
import Store from '../../store';
import Actions from '../../store/actions';

import themev5 from '../../themev5';
import Styles from './CategorizationTable.styles';
import styles from '../Datagrid/Datagrid.styles';
import DatagridFooter from '../Datagrid/DatagridFooter';
import DatagridNoRowsOverlay from '../Datagrid/DatagridNoRowsOverlay';

// api
import { getFDALetters } from '../../api/FDALetters';
import ApplicationEditModal from './ApplicationEditModal';
import TherapeuticAreaEdit from './TherapeuticAreaEdit';

const ComplianceCuration = () => {
  const { dispatch } = useContext<any>(Store);
  const classes = Styles();
  const [pageSize, setPageSize] = useState(10);
  const [letterData, setLetterData] = useState<Array<any>>([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalRowCount, setTotalRowCount] = useState();
  const [loading, setloading] = useState(true);
  const [applicationEditVisible, setApplicationEditDialogVisible] = useState(false);
  const [therapeuticAreaEditVisible, setTherapeuticAreaEditVisible] = useState(false);
  const [searchText, setSearchText] = useState<string>('');
  const [status, setStatus] = useState<string>('all');
  const rowsPerPageOptions = [10, 20, 50, 100];
  const [reload, setReload] = useState(false);

  const [rowForCuration, setSelectedRowForCuration] = useState<any>();
  const [selectedApplication, setSelectedApplication] = useState<any>();
  const [therapeuticAreaStatus, setTherapeuticAreaStatus] = useState<string>('all');

  const debouncedChangeHandler = () => {
    setReload(prevState => !prevState);
  };
  const handleChange = debounce(debouncedChangeHandler, 300);

  useEffect(() => {
    const fetchLetterData = async () => {
      try {
        const result = await getFDALetters(
          pageNumber,
          pageSize,
          status,
          therapeuticAreaStatus,
          searchText.trim().toLowerCase()
        );

        if (result === undefined) {
          throw new Error('Result is undefined');
        }
        if (result.data && result.data.body.letters) {
          setloading(false);
          const resultData = result.data.body.letters;
          setTotalRowCount(result.data.body.count);
          setLetterData(resultData?.map((item: any, index: any) => ({ ...item, id: index })));
        } else {
          setloading(false);
          throw Error('No data');
        }
      } catch (e: any) {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: e.message }
        });
      }
    };
    fetchLetterData();
  }, [pageNumber, status, pageSize, reload, searchText, therapeuticAreaStatus]);

  const closeDialogues = () => {
    setApplicationEditDialogVisible(false);
    setTherapeuticAreaEditVisible(false);
  };
  const closeEdit = () => {
    closeDialogues();
    setReload(prevState => !prevState);
    setSelectedRowForCuration(undefined);
    setSelectedApplication(undefined);
  };
  const closeTherapeuticAreaEdit = () => {
    setTherapeuticAreaEditVisible(false);
    setReload(prevState => !prevState);
    setSelectedApplication(undefined);
  };

  const getQCButtonClass = (sts: string): any => {
    const qcStatus: string = sts.toLowerCase();
    const qcStatusColorMap: any = {
      qced: classes.Button,
      to_qc: classes.BlueButton,
      default: classes.YellowButton
    };
    return qcStatusColorMap[qcStatus] || qcStatusColorMap.default;
  };
  const columns = [
    {
      field: 'issue_date',
      headerName: 'Issue Date',
      width: 200,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params: any) => {
        const { issue_date: issueDate } = params.row;
        return format(new Date(issueDate), 'yyyy-MM-dd');
      }
    },
    {
      field: 'subject',
      headerName: 'Subject',
      width: 500,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'source_page',
      headerName: 'Source Page',
      width: 170,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'application_qc_status',
      headerName: 'Application QC Status',
      width: 200,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'ta_qc_status',
      headerName: 'Therapeutic Area QC Status',
      width: 200,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'Actions',
      width: 450,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: any) => {
        const handleTherapeuticAreaClick = () => {
          const applications = params.row.applications || [];
          if (applications.length > 0) {
            setSelectedApplication({
              letterId: params.row.fda_letter_id,
              applications: applications,
              taQcStatus: params.row.ta_qc_status
            });
            setTherapeuticAreaEditVisible(true);
          }
        };

        return (
          <Box
            style={{
              margin: '3px',
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
              gap: '8px'
            }}>
            <Button
              variant='contained'
              className={getQCButtonClass(params.row.application_qc_status || 'to_qc')}
              onClick={() => {
                setSelectedRowForCuration(params.row);
                setApplicationEditDialogVisible(true);
              }}>
              <Typography variant='body1'>Application Curation</Typography>
            </Button>
            <Button
              variant='contained'
              className={getQCButtonClass(params.row.ta_qc_status || 'to_qc')}
              onClick={handleTherapeuticAreaClick}
              disabled={!params.row.applications || params.row.applications.length === 0}>
              <Typography variant='body1'>Therapeutic Area Curation</Typography>
            </Button>
          </Box>
        );
      }
    }
  ];
  return (
    <ThemeProvider theme={themev5}>
      <CssBaseline />
      <Grid container>
        <Grid item xl={10} lg={10}>
          <Box display='flex' ml={2} mt={10} alignItems='center'>
            <TextField
              variant='outlined'
              type='search'
              id='search'
              label='Search Letter Data'
              value={searchText}
              InputProps={{
                style: { fontSize: '1.5vh', minWidth: '250px' }
              }}
              InputLabelProps={{
                style: { fontSize: '1.5vh' }
              }}
              onChange={e => {
                setSearchText(e.target.value);
                setPageNumber(0);
                handleChange();
              }}
            />
            <TextField
              variant='outlined'
              id='status'
              label='QC Status'
              select
              value={status}
              onChange={e => setStatus(e.target.value)}
              InputProps={{
                style: { fontSize: '1.5vh', minWidth: '250px' }
              }}
              InputLabelProps={{
                style: { fontSize: '1.5vh' }
              }}>
              <MenuItem value='all'>All Documents</MenuItem>
              <MenuItem value='qced'>Qced</MenuItem>
              <MenuItem value='to_qc'>To QC</MenuItem>
              <MenuItem value='escalate'>Escalate</MenuItem>
              <MenuItem value='missing details'>Missing Details</MenuItem>
              <MenuItem value='not applicable'>Not Applicable</MenuItem>
            </TextField>
            <TextField
              variant='outlined'
              id='therapeuticArea'
              label='Therapeutic Area Status'
              select
              value={therapeuticAreaStatus}
              onChange={e => setTherapeuticAreaStatus(e.target.value)}
              InputProps={{
                style: { fontSize: '1.5vh', minWidth: '250px' }
              }}
              InputLabelProps={{
                style: { fontSize: '1.5vh' }
              }}>
              <MenuItem value='all'>All Documents</MenuItem>
              <MenuItem value='qced'>Qced</MenuItem>
              <MenuItem value='to_qc'>To QC</MenuItem>
              <MenuItem value='escalate'>Escalate</MenuItem>
              <MenuItem value='missing details'>Missing Details</MenuItem>
              <MenuItem value='not applicable'>Not Applicable</MenuItem>
            </TextField>
          </Box>
        </Grid>
      </Grid>
      <Box
        height='80vh'
        p={2}
        mt={1}
        display='flex'
        alignItems='center'
        justifyContent='center'
        className={classes.root}>
        <DataGrid
          sx={styles.datagrid}
          rowSpacingType='margin'
          sortingOrder={['desc', 'asc']}
          checkboxSelection={false}
          density='standard'
          rows={letterData}
          columns={columns as any}
          rowCount={totalRowCount}
          pageSize={pageSize}
          getRowId={row => row.id}
          onPageSizeChange={newPageSize => {
            setPageSize(newPageSize);
            setPageNumber(0);
          }}
          getRowHeight={() => 'auto'}
          onPageChange={setPageNumber}
          rowsPerPageOptions={rowsPerPageOptions}
          pagination
          loading={loading}
          getRowClassName={() => `table-rows`}
          page={pageNumber}
          paginationMode='server'
          components={{
            LoadingOverlay: LinearProgress,
            Footer: DatagridFooter,
            NoRowsOverlay: DatagridNoRowsOverlay
          }}
        />
        <Dialog
          open={applicationEditVisible}
          onClose={closeEdit}
          sx={{ backdropFilter: 'blur(5px)' }}
          maxWidth='xl'
          fullWidth>
          {rowForCuration && applicationEditVisible ? (
            <ApplicationEditModal closeEdit={closeEdit} letterData={rowForCuration} />
          ) : null}
        </Dialog>
        <Dialog
          open={therapeuticAreaEditVisible}
          onClose={closeTherapeuticAreaEdit}
          sx={{ backdropFilter: 'blur(5px)' }}
          maxWidth='xl'
          fullWidth>
          {selectedApplication && therapeuticAreaEditVisible ? (
            <TherapeuticAreaEdit
              letterId={selectedApplication.letterId}
              applications={selectedApplication.applications}
              taQcStatus={selectedApplication.taQcStatus}
              closeEdit={closeTherapeuticAreaEdit}
            />
          ) : null}
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default ComplianceCuration;
